import React, {ReactElement, useState} from "react";
import {GridCriteriaPanel} from "./GridCriteriaPanel";
import {ConfigSection, Grid, GridConfig} from "./Grid";
import {PagingInfo, SortState} from "../../../utils/Grid";
import './GridPage.css'
import {ToolRow} from "./ToolRow";
import {ButtonProps, Collapse} from "reactstrap";

interface GridPageProps<T> {
    gridConfig: GridConfig<T>;
    defaultCriteria?: any;
    records: T[];
    sort: SortState<T>;
    moreRecordsAvailable?: boolean;
    newLink?: string;
    newLinkText?: string;
    shouldShowCriteriaPanel: boolean;
    noRecordsMessage?: string;
    title?: string;
    printable?: boolean;
    isLoading?: boolean;
    extraActions?: ButtonProps[];
    onShouldFetchMore?: (paging: PagingInfo) => void;
    onSortChanged: (newSort: SortState<T>) => void;
    onSearchChange?: (search: string) => void;
    onCriteriaChange?: (criteria: any) => void;
    onResetCriteria?: () => void;
    onShouldDisplayMore?: () => void;
    onShouldRefetch?: () => void;
    onRowClick?: (record: T) => void;
    onNewModalButtonClicked?: () => void;
    onDownload?: () => void;
    renderCriteriaPanel?: (params: RenderGridCriteriaParams<T>) => ReactElement;

    /**
     * Render table row(s) to be placed inside of the table's footer (`tfoot`) element
     * If the property is not defined no `tfoot` will be rendered.
     *
     * The function should return a single `tr` or a ReactFragment containing
     * multiple `tr`s
     *
     * @returns JSX.Element to be placed in tfoot
     */
    renderGridFooter?: Parameters<typeof Grid<T>>[0]['renderGridFooter']
    disableSearch?: boolean;
}

export function GridPage<T> ({
    defaultCriteria,
    extraActions,
    gridConfig: {actions, configSections},
    isLoading,
    moreRecordsAvailable,
    newLink,
    newLinkText,
    noRecordsMessage,
    onCriteriaChange,
    onDownload,
    onNewModalButtonClicked,
    onResetCriteria,
    onRowClick,
    onSearchChange,
    onShouldDisplayMore,
    onShouldFetchMore,
    onShouldRefetch,
    onSortChanged,
    printable,
    records,
    shouldShowCriteriaPanel,
    sort,
    title,
    renderCriteriaPanel,
    renderGridFooter,
    disableSearch,
}: GridPageProps<T>) {
    
  const [criteriaIsOpen, setCriteriaIsOpen] = useState(true);
  const columns = configSections?.flatMap(section => section.columns);

  return (
    <div className='d-flex w-100 h-100'>
        {shouldShowCriteriaPanel &&
            <div className="d-flex flex-column grid-criteria-container mx-2">
                <Collapse horizontal isOpen={criteriaIsOpen}>
                    <div id="CriteriaContainer">
                        {renderCriteriaPanel ?
                            renderCriteriaPanel({
                                criteria: defaultCriteria,
                                configSections,
                            }) :
                            <GridCriteriaPanel
                                configSections={configSections}
                                defaultCriteria={defaultCriteria}
                                onSearch={(onCriteriaChange)!}
                                onShouldRefetch={onShouldRefetch}
                                onResetCriteria={onResetCriteria}
                                disableSearch={disableSearch}
                            />
                        }
                    </div>
                </Collapse>
            </div>
        }
      <div className='d-flex flex-column grid-page-right-side-content'>
        <ToolRow shouldShowCriteriaPanel={shouldShowCriteriaPanel}
                 title={title}
                 printable={printable}
                 onDownload={onDownload}
                 newLink={newLink}
                 newLinkText={newLinkText}
                 onNewModalButtonClicked={onNewModalButtonClicked}
                 onSearchChange={onSearchChange}
                 toggleCriteriaText={criteriaIsOpen ? 'Close' : 'Open'}
                 onShowCriteriaToggle={() => setCriteriaIsOpen(curr => !curr)}
                 extraActions={extraActions}/>
        <Grid records={records}
              columns={columns}
              actions={actions}
              sort={sort}
              onSortChanged={onSortChanged}
              onShouldFetchMore={onShouldFetchMore}
              onShouldDisplayMore={onShouldDisplayMore}
              moreRecordsAvailable={moreRecordsAvailable}
              noRecordsMessage={noRecordsMessage}
              isLoading={isLoading}
              onRowClick={onRowClick}
                renderGridFooter={renderGridFooter}
        />
      </div>
    </div>
  );
}

interface RenderGridCriteriaParams<CriteriaType> {
    criteria: CriteriaType;
    configSections: ConfigSection<CriteriaType>[]
}